// ============================================================
// agent_orders_controller.js
// 게임 안 에이전트 "현재 지시" 패널 컨트롤러
//
// 동작 방식:
//   - 입력할 때마다 280자 카운터 갱신 (상한의 정본은 D서버, 여기는 사용자 편의)
//   - 폼 submit 을 가로채 인플레이스 전송(fetch) — 페이지를 이동하지 않는다
//   - 전송 성공 시 이력 프레임을 즉시 한 번 갱신 (5초를 기다리지 않게)
//   - 5초 간격으로 이력 프레임만 재로딩 → 배지가 pending → delivered → acked 로 따라간다
//
// 사용 위치: app/views/agents/_orders_panel.html.erb
// 엔드포인트: POST /agents/:id/orders (전송) · GET /agents/:id/orders (프레임 갱신)
//
// 주의사항:
//   - ⚠️ JS 가 없거나 죽어도 폼은 일반 제출로 동작해야 한다(local: true + 서버 리다이렉트).
//     submit 가로채기는 개선이지 전제가 아니다.
//   - ⚠️ 갱신 대상은 이력 프레임뿐이다. 컴포저까지 갈아치우면 입력 중인 글자가 날아간다.
//   - 5초는 D서버 폴 간격(10초)의 절반이다. 에이전트가 지시를 가져간 사실이
//     한 주기 안에 화면에 반영되도록 의도적으로 더 촘촘하게 잡았다.
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {

  // input: 지시 입력 textarea / counter: 남은 글자 표시 / submitButton: 중복 전송 잠금 대상
  static targets = ["input", "counter", "submitButton"]
  // max: 카운터 상한(280) / refreshUrl: 이력 프레임을 다시 받아올 GET 경로
  // errorFallback: 서버 사유를 못 받았을 때(네트워크 장애 등) 띄울 문구.
  //   ⚠️ JS 는 I18n 을 못 부르므로 번역된 문자열을 뷰가 주입한다(하드코딩 금지 규칙 준수).
  static values  = { max: Number, refreshUrl: String, errorFallback: String }

  // ── connect() ─────────────────────────────────────────────
  // 초기 카운터 동기화(브라우저가 값을 복원한 경우 대비) 후 5초 폴링 시작
  connect() {
    this.count()
    this._timer = setInterval(() => this._refreshFrame(), 5_000)
  }

  // ── disconnect() ──────────────────────────────────────────
  // DOM 제거 시 인터벌 정리 (메모리 누수·유령 폴링 방지)
  disconnect() {
    clearInterval(this._timer)
  }

  // ── count() ───────────────────────────────────────────────
  // 입력 길이를 카운터에 반영한다. 상한 초과 표시는 색으로만 알리고 입력을 막지는 않는다
  // (maxlength 가 이미 걸려 있고, 최종 판정은 D서버가 한다).
  count() {
    if (!this.hasCounterTarget || !this.hasInputTarget) return

    const used = this.inputTarget.value.length
    const max  = this.maxValue || 280
    this.counterTarget.textContent = `${used} / ${max}`
    this.counterTarget.style.color = used > max ? "var(--danger, #ff5c5c)" : "var(--text-tertiary)"
  }

  // ── submit(event) ─────────────────────────────────────────
  // 폼 제출을 가로채 fetch 로 보낸다. 성공하면 입력을 비우고 이력 프레임을 즉시 갱신한다.
  // 실패 사유는 서버가 번역해 내려주므로 그대로 보여준다(여기서 문구를 만들지 않는다).
  submit(event) {
    // 전송 중 중복 클릭 잠금 — 연타는 D서버 레이트리밋에도 걸린다.
    if (this._sending) {
      event.preventDefault()
      return
    }

    const form = event.target
    const text = this.hasInputTarget ? this.inputTarget.value.trim() : ""
    if (text === "") return  // 빈 입력은 서버 폴백(일반 제출)에 맡긴다

    event.preventDefault()
    this._sending = true
    this._lockSubmit(true)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(form.action, {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept":       "application/json",
        "X-CSRF-Token": csrfToken || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ text: text })
    })
    .then(res => res.json().then(body => ({ ok: res.ok, body })))
    .then(({ ok, body }) => {
      if (ok) {
        // 전송 성공 — 입력을 비우고 이력을 바로 갱신한다(5초를 기다리지 않는다).
        this.inputTarget.value = ""
        this.count()
        this._refreshFrame()
      } else {
        // 서버가 번역해 준 사유를 그대로 노출한다(길이 초과·연타 제한·슬롯 없음 등).
        window.alert(body.error || this.errorFallbackValue)
      }
    })
    .catch(() => {
      // 네트워크 장애 — 사유를 서버에서 못 받았으므로 뷰가 주입한 폴백 문구를 쓴다.
      window.alert(this.errorFallbackValue)
    })
    .finally(() => {
      this._sending = false
      this._lockSubmit(false)
    })
  }

  // ── Private: _lockSubmit(locked) ──────────────────────────
  // 전송 중 제출 버튼 비활성 토글 (중복 전송 방지)
  _lockSubmit(locked) {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = locked
    this.submitButtonTarget.style.opacity = locked ? "0.5" : "1"
  }

  // ── Private: _refreshFrame() ──────────────────────────────
  // 이력 Turbo Frame 만 다시 로드한다. reload() 는 프레임의 src 를 그대로 다시 부르므로
  // 컴포저(프레임 밖)는 건드리지 않는다.
  _refreshFrame() {
    const frame = document.getElementById("agent-orders-list")
    if (!frame) return

    // src 가 없으면(최초 서버 렌더 상태) refreshUrl 을 심어 준다 — 이후에는 reload 만으로 갱신된다.
    if (!frame.src && this.hasRefreshUrlValue) {
      frame.src = this.refreshUrlValue
      return
    }

    frame.reload()
  }
}
